import { Post } from "src/api/client-api/post/postSchema";
import { SsqSsqHeading, SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { PagesContent } from "./PagesContent";
import { ScoreChart } from "src/components/ScoreChart/ScoreChart";
import { QuestionnaireLayout } from "./layouts/QuestionnaireLayout";
import { ScoreSection } from "./layouts/ScoreSection";
import { useGetPostScores } from "src/api/client-api/olapScores/useGetPostScores";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { ScoreService } from "src/services/ScoreService";
import { useEffect, useState } from "react";
import { OlapScoresResult } from "src/api/client-api/olapScores/olapScoresSchema";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { extractDataListVersionIdsFromSsqVersion } from "src/utils/ssq";
import { useGetAllDataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { getSameLevelDimensionsChartData } from "src/utils/questionnaire";

export type useQuestionnaireUpdatePagesProps = {
    ssqSsqHeadings: SsqSsqHeading[];
    ssqVersion?: SsqVersion;
    post?: Post;
    isPostLoading?: boolean;
    changePost: (callable: (post: Post) => void) => void;
    allowComments?: boolean;
};
export const useQuestionnaireUpdatePages = (props: useQuestionnaireUpdatePagesProps) => {
    const { ssqVersion, ssqSsqHeadings, post, isPostLoading, changePost, allowComments = true } = props;
    const { scoresWithVariations, diagnosticScoresWithVariations, lastSelectedVariation } = useEntityScores();

    const [olapScores, setOlapScores] = useState<OlapScoresResult | undefined>();

    const scoresParams = useOlapScoresParams({
        scores: {
            ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
            dimensions: [
                [["headings"], ["subheadings"], ["properties"], ["multivalues"], ["subproperties"]],
                [["ssq_versions"], ["headings"]],
            ],
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
    });

    const {
        data: scores,
        isLoading: areScoresLoading,
        isRefetching: areScoresRefetching,
    } = useGetPostScores({
        postId: post?.id,
        scoresQuery: scoresParams.query,
    });

    const hasScores =
        scoresWithVariations.filter((scoreWithVariation) => scoreWithVariation.displayMode !== "regulation").length > 0;
    const scoreId = lastSelectedVariation.scoreId;
    const mainScore = scores && scoreId ? scores.olapScores[scoreId] : undefined;

    const dataListVersionIds = extractDataListVersionIdsFromSsqVersion(ssqVersion, post?.hiddenElements);
    const { overrides } = useGetAllDataListValueOverride({ dataListVersionIds });

    useEffect(() => {
        scoresParams.state.setQuery({
            scores: {
                ...scoresParams.query.scores,
                ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
                variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            },
        });
    }, [lastSelectedVariation]);

    useEffect(() => {
        setOlapScores(scores?.olapScores);
    }, [scores]);

    const isLoading = Boolean(isPostLoading || (hasScores && (areScoresLoading || areScoresRefetching)));

    const pages = ssqSsqHeadings.map((ssqSsqHeading: SsqSsqHeading) => {
        return {
            id: ssqSsqHeading.id,
            title: ssqSsqHeading.ssqHeading.title,
            content: (
                <QuestionnaireLayout
                    hasScores={hasScores}
                    isLoading={Boolean(isPostLoading)}
                    scoreSection={
                        <ScoreSection>
                            {(() => {
                                const { data, totalScore, maxScore } = getSameLevelDimensionsChartData(
                                    olapScores?.[lastSelectedVariation.scoreId],
                                    ["headings", "ssq_versions"],
                                    { ssqVersion },
                                );

                                return (
                                    <ScoreChart
                                        scoresWithVariations={diagnosticScoresWithVariations}
                                        selectedVariation={lastSelectedVariation}
                                        isLoading={areScoresLoading || areScoresRefetching}
                                        data={data || undefined}
                                        totalScore={totalScore}
                                        maxScore={maxScore}
                                    />
                                );
                            })()}
                        </ScoreSection>
                    }
                >
                    <PagesContent
                        ssqSsqHeading={ssqSsqHeading}
                        post={post}
                        changePost={changePost}
                        allowComments={allowComments}
                        unit={mainScore?.unit}
                        olapScores={olapScores}
                        setOlapScores={setOlapScores}
                        dataListValueOverrides={overrides}
                    />
                </QuestionnaireLayout>
            ),
        };
    });

    return { pages, isLoading };
};
